import type { EditorHost } from '@blocksuite/block-std';
import {
  type AffineAIPanelWidgetConfig,
  type AIItemGroupConfig,
  BlocksUtils,
  type CopilotSelectionController,
  MindmapElementModel,
  type ShapeElementModel,
  TextElementModel,
} from '@blocksuite/blocks';
import type { TemplateResult } from 'lit';

import {
  actionToGenerating,
  defaultHandler,
  extraConditions,
  responses,
} from '../actions/edgeless-response';
import type { CtxRecord } from '../actions/types';
import { createMermaidDataProcessor } from '../messages/mermaid-renderer';
import { createMindmapDataProcessor } from '../messages/mindmap';
import { createQuizDataProcessor } from '../messages/quiz-processor';
import { createSlidesDataProcessor } from '../messages/slides-processor';
import { createTextDataProcessor } from '../messages/text';
import { createIframeDataProcessor } from '../messages/wrapper';
import { AIProvider } from '../provider';
import {
  chainTextBlockShowWhen,
  getAIPanel,
  getEdgelessAndElements,
  getEdgelessCopilotWidget,
  getElementToolbar,
  isMindmapChild,
  isMindMapRoot,
  mindmapChildShowWhen,
  mindMapToMarkdown,
  noteWithoutPiqueBlocksAndMindmapShowWhen,
} from '../utils/edgeless';
import { canvasToBlob } from '../utils/image';
import {
  bindTextStream,
  createGenerationHandler,
  getContentFromSelected,
  getCopilotSelectedElems,
  getDefaultCustomInput,
  getEdgelessRootFromEditor,
  getEdgelessService,
  getSelectedNoteAnchor,
  getSelections,
  selectAboveBlocks,
} from '../utils/selection-utils';
import {
  AIExpandMindMapIcon,
  AIImageIconWithAnimation,
  AIMindMapIcon,
  AIMindMapIconWithAnimation,
  AIPenIconWithAnimation,
  AIPresentationIcon,
  AIPresentationIconWithAnimation,
  AIStarIconWithAnimation,
  AIWhiteboardIcon,
  MakeItRealIcon,
  MakeItRealIconWithAnimation,
} from './icons';

// ===========================
// TYPE DEFINITIONS
// ===========================

type AnswerRenderer = NonNullable<any>['answerRenderer'];

// ===========================
// AI ACTION GROUP CONFIGURATIONS
// ===========================

export const GenerateWithAIGroup: AIItemGroupConfig = {
  name: '',
  items: [
    {
      name: 'Explain this using a mind map',
      icon: AIMindMapIcon,
      showWhen: chainTextBlockShowWhen,
      handler: edgelessHandler('brainstormMindmap', AIPenIconWithAnimation),
    },
    {
      name: 'Create Summary Slides',
      icon: AIPresentationIcon,
      showWhen: chainTextBlockShowWhen,
      handler: edgelessHandler('createSlides', AIPresentationIconWithAnimation),
    },
    {
      name: 'Create Flowchart',
      icon: AIWhiteboardIcon,
      showWhen: chainTextBlockShowWhen,
      handler: edgelessHandler('createMermaid', AIPenIconWithAnimation),
    },
    {
      name: 'Create Quiz',
      icon: AIWhiteboardIcon,
      showWhen: chainTextBlockShowWhen,
      handler: edgelessHandler('createQuiz', AIPenIconWithAnimation),
    },
  ],
};

export const EdgelessAIActionGroup: AIItemGroupConfig = {
  name: '',
  items: [
    {
      name: 'Explain this visually',
      icon: AIPresentationIcon,
      // Pique Update - Hide the explain this visually action until it is fixed
      showWhen: () => false,
      handler: edgelessHandler('createImage', AIImageIconWithAnimation),
    },
    {
      name: 'Add detail',
      icon: AIExpandMindMapIcon,
      showWhen: mindmapChildShowWhen,
      handler: edgelessHandler(
        'expandMindmap',
        AIMindMapIconWithAnimation,
        undefined,
        undefined,
        async function (host) {
          const selected = getCopilotSelectedElems(host);
          const firstSelected = selected[0] as ShapeElementModel;
          const mindmap = firstSelected?.group;

          if (!(mindmap instanceof MindmapElementModel)) {
            return { attachments: [], content: '' };
          }

          const content = firstSelected.text?.toString() ?? '';
          const mindmapMarkdown = mindMapToMarkdown(mindmap);

          return {
            attachments: [],
            content: `${content}\\n\\nMindmap:\\n${mindmapMarkdown}`,
          };
        }
      ),
    },
    {
      name: 'Explain this using a mind map',
      icon: AIMindMapIcon,
      showWhen: noteWithoutPiqueBlocksAndMindmapShowWhen,
      handler: edgelessHandler('brainstormMindmap', AIMindMapIconWithAnimation),
    },
    {
      name: 'Regenerate mind map',
      icon: AIMindMapIcon,
      showWhen: () => false,
      // Pique Update - Hide the regenerate mind map action
      // showWhen: mindmapRootShowWhen,
      handler: edgelessHandler(
        'brainstormMindmap',
        AIMindMapIconWithAnimation,
        {
          regenerate: true,
        }
      ),
    },
    {
      name: 'Make it real',
      icon: MakeItRealIcon,
      // Pique Update - Hide the make it real action
      showWhen: () => false,
      handler: edgelessHandler(
        'makeItReal',
        MakeItRealIconWithAnimation,
        undefined,
        undefined,
        async host => {
          const selectedElements = getCopilotSelectedElems(host);

          // from user input
          if (selectedElements.length === 0) {
            const content = getAIPanel(host)?.inputText?.trim();
            if (!content) return { attachments: [], content: '' };
            return {
              attachments: [],
              content,
            };
          }

          const { notes, frames, shapes, images, edgelessTexts } =
            BlocksUtils.splitElements(selectedElements);
          const f = frames.length;
          const i = images.length;
          const n = notes.length;
          const s = shapes.length;
          const e = edgelessTexts.length;

          if (f + i + n + s + e === 0) {
            return { attachments: [], content: '' };
          }

          // single note, text
          if (
            i === 0 &&
            n + s + e === 1 &&
            (n === 1 ||
              e === 1 ||
              (s === 1 && shapes[0] instanceof TextElementModel))
          ) {
            return {
              attachments: [],
              content: '',
            };
          }

          // from user input
          let content = '';
          if (content.length === 0) {
            content = getAIPanel(host)?.inputText?.trim() || '';
          }

          const edgelessRoot = getEdgelessRootFromEditor(host);
          const canvas = await edgelessRoot.clipboardController.toCanvas(
            [...notes, ...frames, ...images],
            shapes,
            {
              dpr: 1,
              background: 'white',
            }
          );
          if (!canvas) return { attachments: [], content };
          const png = await canvasToBlob(canvas);
          if (!png) return { attachments: [], content };

          return {
            content,
            attachments: [png],
          };
        }
      ),
    },
  ],
};

export const AIItemGroups: AIItemGroupConfig[] = [
  // Pique Update - commenting out options available
  GenerateWithAIGroup,
];

export const edgelessActionGroups = [EdgelessAIActionGroup];

// ===========================
// DATA PROCESSOR FUNCTIONS
// ===========================

export function createEdgelessResponseDataProcessor<
  T extends keyof BlockSuitePresets.AIActions,
>(id: T, host: EditorHost, ctx: CtxRecord): AnswerRenderer {
  switch (id) {
    case 'brainstormMindmap': {
      const selectedElements = ctx.get()[
        'selectedElements'
      ] as BlockSuite.EdgelessModel[];
      const firstElement = selectedElements[0];
      if (
        firstElement &&
        (isMindMapRoot(firstElement) || isMindmapChild(firstElement))
      ) {
        const mindmap = firstElement.group as MindmapElementModel;
        return createMindmapDataProcessor(host, ctx, mindmap.style);
      }
      return createMindmapDataProcessor(host, ctx);
    }
    case 'expandMindmap':
      return createMindmapDataProcessor(host, ctx);
    case 'createSlides':
      return createSlidesDataProcessor(host, ctx);
    case 'makeItReal':
      return createIframeDataProcessor(host, { height: 300 });
    case 'createQuiz':
      return createQuizDataProcessor(host, ctx);
    case 'createMermaid': {
      return createMermaidDataProcessor(host, ctx);
    }
    default:
      return createTextDataProcessor(host, { maxHeight: 320 });
  }
}

// ===========================
// CONFIGURATION BUILDER FUNCTIONS
// ===========================

export function buildGeneratingConfig(generatingIcon?: TemplateResult<1>) {
  return {
    generatingIcon: generatingIcon ?? AIStarIconWithAnimation,
  };
}

export function buildAIPanelConfig(panel: {
  host: EditorHost;
  hide: () => void;
}): AffineAIPanelWidgetConfig {
  return {
    answerRenderer: createTextDataProcessor(panel.host, { maxHeight: 320 }),
    finishStateConfig: {
      responses: [],
      actions: [],
    },
    generatingStateConfig: buildGeneratingConfig(),
    errorStateConfig: {
      error: undefined,
      login: () => {},
      upgrade: () => {},
      cancel: () => panel.hide(),
      responses: [],
    },
    copy: {
      allowed: false,
      onCopy: () => false,
    },
  };
}

interface AIPanelLike {
  host: EditorHost;
  config?: AffineAIPanelWidgetConfig | null;
  hide?: () => void;
  updateComplete?: Promise<boolean>;
}

export function updateEdgelessAIPanelConfig<
  T extends keyof BlockSuitePresets.AIActions,
>(
  aiPanel: AIPanelLike,
  id: T,
  generatingIcon: TemplateResult<1>,
  ctx: CtxRecord,
  variants?: Omit<
    Parameters<BlockSuitePresets.AIActions[T]>[0],
    keyof BlockSuitePresets.AITextActionOptions
  >,
  customInput?: (
    host: EditorHost,
    ctx: CtxRecord
  ) => Promise<{
    input?: string;
    content?: string;
    attachments?: (string | Blob)[];
    seed?: string;
  } | void>,
  _trackerOptions?: BlockSuitePresets.TrackerOptions
) {
  const host = aiPanel.host;
  const { config } = aiPanel;
  if (!config) {
    return;
  }

  const edgelessCopilot = getEdgelessCopilotWidget(host);

  config.answerRenderer = createEdgelessResponseDataProcessor(id, host, ctx);
  config.generateAnswer = createGenerationHandler(
    id,
    variants,
    customInput
  )(host, ctx);

  config.generatingStateConfig = actionToGenerating(id, generatingIcon);

  const configWithAutoInsert = config as AffineAIPanelWidgetConfig & {
    autoInsertHandler?: () => Promise<void>;
  };
  configWithAutoInsert.autoInsertHandler = async () => {
    const processedData = ctx.get();
    const canExecute = extraConditions[id] || (() => true);
    const edgelessResponseHandler = responses[id] ?? defaultHandler;

    if (canExecute(processedData)) {
      await edgelessResponseHandler(host, ctx);
    }
  };

  config.errorStateConfig = {
    error: undefined,
    login: () => {},
    upgrade: () => {},
    cancel: () => aiPanel.hide?.(),
    responses: [],
  };

  config.finishStateConfig = {
    responses: [],
    actions: [],
  };

  config.copy = {
    allowed: false,
    onCopy: () => false,
  };

  config.hideCallback = () => {
    const complete = aiPanel.updateComplete ?? Promise.resolve(true);
    complete
      .finally(() => {
        const edgelessService = host.std.spec.getService(
          'affine:page'
        ) as unknown as {
          tools?: {
            switchToDefaultMode?: (opts: {
              elements: unknown[];
              editing: boolean;
            }) => void;
          };
        };
        edgelessService.tools?.switchToDefaultMode?.({
          elements: [],
          editing: false,
        });
        host.selection.clear();
        edgelessCopilot.lockToolbar(false);
      })
      .catch(console.error);
  };
}

// ===========================
// MAIN HANDLER FUNCTIONS
// ===========================

export function edgelessHandler<T extends keyof BlockSuitePresets.AIActions>(
  id: T,
  generatingIcon: TemplateResult<1>,
  variants?: Omit<
    Parameters<BlockSuitePresets.AIActions[T]>[0],
    keyof BlockSuitePresets.AITextActionOptions
  >,
  trackerOptions?: BlockSuitePresets.TrackerOptions,
  customInputFn?: (host: EditorHost) => Promise<{
    attachments: Blob[];
    content: string;
  }>
) {
  return ((host: EditorHost) => {
    // First expand the edgeless view to large early
    const edgelessService = getEdgelessService(host);
    edgelessService?.setFirstViewToLarge();

    void getEdgelessAndElements(host)
      .then(edgelessResult => {
        if (!edgelessResult) return;

        const {
          edgeless,
          updatedSelectedElements,
          host: updatedHost,
        } = edgelessResult;

        const edgelessWithTool = edgeless as unknown as {
          tool?: {
            setEdgelessTool?: (opts: { type: string }) => void;
            controllers?: Record<string, unknown>;
          };
          service?: {
            tool?: { controllers?: Record<string, unknown> };
          };
        };

        if (edgelessWithTool.tool?.setEdgelessTool) {
          edgelessWithTool.tool.setEdgelessTool({ type: 'copilot' });
        }

        let currentController: CopilotSelectionController | undefined;
        try {
          if (
            edgelessWithTool.tool?.controllers &&
            'copilot' in edgelessWithTool.tool.controllers
          ) {
            currentController = edgelessWithTool.tool.controllers[
              'copilot'
            ] as CopilotSelectionController;
          }

          if (
            !currentController &&
            edgelessWithTool.service?.tool?.controllers
          ) {
            if ('copilot' in edgelessWithTool.service.tool.controllers) {
              currentController = edgelessWithTool.service.tool.controllers[
                'copilot'
              ] as CopilotSelectionController;
            }
          }
        } catch (error) {
          console.warn('Failed to get copilot controller:', error);
        }

        try {
          if (
            currentController &&
            updatedSelectedElements.length > 0 &&
            updatedSelectedElements.every(element => element?.xywh)
          ) {
            currentController.updateSelectionWith(updatedSelectedElements, 10);
            // Force emit the draggingAreaUpdated event
            currentController.draggingAreaUpdated.emit(true);
          }
        } catch (error) {
          console.warn('Failed to update copilot selection:', error);
          // Continue with the action even if selection update fails
        }

        const defaultCustomInput = () => getDefaultCustomInput(updatedHost);

        // Pique Update - Add a small delay to allow the copilot widget to update with the controller's selection area
        setTimeout(() => {
          const aiPanel = getAIPanel(updatedHost);
          if (!aiPanel) return;
          const edgelessCopilot = getEdgelessCopilotWidget(updatedHost);
          let internal: Record<string, unknown> = {};
          const selectedElements = getCopilotSelectedElems(updatedHost);
          if (!selectedElements) return;
          const { selectedBlocks } = getSelections(updatedHost);
          const ctx = {
            get() {
              return {
                ...internal,
                selectedElements,
              };
            },
            set(data: Record<string, unknown>) {
              internal = data;
            },
          };

          edgelessCopilot.hideCopilotPanel();
          edgelessCopilot.lockToolbar(true);

          updateEdgelessAIPanelConfig(
            aiPanel,
            id,
            generatingIcon,
            ctx,
            variants,
            customInputFn
              ? async () => await customInputFn(updatedHost)
              : defaultCustomInput,
            trackerOptions
          );

          const elementToolbar = getElementToolbar(updatedHost);
          const isEmpty = selectedElements.length === 0;
          const isMakeItRealAction = id === 'makeItReal';
          let referenceElement = null;
          let togglePanel = () => Promise.resolve(isEmpty);

          if (selectedBlocks && selectedBlocks.length !== 0) {
            referenceElement = selectedBlocks.at(-1);
          } else if (edgelessCopilot.visible && edgelessCopilot.selectionElem) {
            referenceElement = edgelessCopilot.selectionElem;
          } else if (elementToolbar.toolbarVisible) {
            referenceElement = getElementToolbar(updatedHost);
          } else if (!isEmpty) {
            const lastSelected = selectedElements.at(-1)?.id;
            if (!lastSelected) return;
            referenceElement = getSelectedNoteAnchor(updatedHost, lastSelected);
          }

          if (!referenceElement) return;

          if (isMakeItRealAction) {
            togglePanel = async () => {
              if (isEmpty) return true;
              const { notes, shapes, images, edgelessTexts, embedSyncedDocs } =
                BlocksUtils.splitElements(selectedElements);
              const blocks = [
                ...notes,
                ...shapes,
                ...images,
                ...edgelessTexts,
                ...embedSyncedDocs,
              ];
              if (blocks.length === 0) return true;
              const content = await getContentFromSelected(updatedHost, blocks);
              ctx.set({
                content,
              });
              return content.length === 0;
            };
          }

          togglePanel()
            .then(isEmpty => {
              aiPanel.toggle(
                referenceElement,
                isEmpty ? undefined : 'placeholder'
              );
            })
            .catch(console.error);
        }, 0);
      })
      .catch(error => {
        console.error('Error in edgelessHandler:', error);
      });

    // Return a promise that resolves immediately to maintain synchronous behavior
    return Promise.resolve() as unknown as void;
  }) as (host: EditorHost) => void;
}

export function handleInlineAskAIAction(host: EditorHost) {
  const panel = getAIPanel(host);
  if (!panel) return;
  const selection = host.selection.find('text');
  const lastBlockPath = selection
    ? (selection.to?.blockId ?? selection.blockId)
    : null;
  if (!lastBlockPath) return;
  const block = host.view.getBlock(lastBlockPath);
  if (!block) return;

  const generateAnswer: AffineAIPanelWidgetConfig['generateAnswer'] = ({
    finish,
    input,
    signal,
    update,
  }) => {
    if (!AIProvider.actions.chat) return;

    // recover selection to get content from above blocks
    if (!selection) return;
    host.selection.set([selection]);

    selectAboveBlocks(host)
      .then(context => {
        if (!AIProvider.actions.chat) return;
        const stream = AIProvider.actions.chat({
          input: `${context}\n${input}`,
          stream: true,
          host,
          where: 'inline-chat-panel',
          control: 'chat-send',
          docId: host.doc.id,
          workspaceId: host.doc.collection.id,
        });
        bindTextStream(stream, { update, finish, signal });
      })
      .catch(console.error);
  };
  if (!panel.config) return;
  panel.config.generateAnswer = generateAnswer;
  panel.toggle(block);
}
